/**
 * SettingsSideNav
 * Category + section navigation for the unified Settings page.
 * Shows Workspace categories and Project categories (when a project is selected).
 */

import React from 'react';
import { Wrench, Github, Bot, Sparkles, FileText, Settings2, GitBranch, Layers, Trash2, Puzzle, Rocket, Shield, Plug, Download, Terminal, Palette } from 'lucide-react';

export type SettingsCategory =
  | 'build' | 'github' | 'appearance' | 'agents' | 'prompts' | 'doc-types' | 'plugins' | 'integrations' | 'tokens' | 'skills'
  | 'deployments' | 'fleet'
  | 'project-general' | 'project-repo' | 'project-agents' | 'project-tools';

interface CategoryDef {
  id: SettingsCategory;
  label: string;
  icon: React.ReactNode;
  sections?: string[];
}

const WORKSPACE_CATEGORIES: CategoryDef[] = [
  {
    id: 'build',
    label: 'Build Defaults',
    icon: <Wrench size={15} />,
    sections: ['Branching', 'PR & Merge', 'Quality Gates', 'Integration Testing', 'Sprites'],
  },
  {
    id: 'github',
    label: 'GitHub',
    icon: <Github size={15} />,
  },
  {
    id: 'appearance',
    label: 'Appearance',
    icon: <Palette size={15} />,
  },
  {
    id: 'agents',
    label: 'Agent Config',
    icon: <Bot size={15} />,
    sections: ['Code Execution', 'Defaults', 'Sprite Instructions'],
  },
  {
    id: 'prompts',
    label: 'AI Prompts',
    icon: <Sparkles size={15} />,
    sections: ['Analysis', 'Planning'],
  },
  {
    id: 'doc-types',
    label: 'Document Types',
    icon: <FileText size={15} />,
  },
  {
    id: 'plugins',
    label: 'Plugins',
    icon: <Puzzle size={15} />,
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: <Plug size={15} />,
  },
  {
    id: 'tokens',
    label: 'Install Martha',
    icon: <Download size={15} />,
  },
  {
    id: 'skills',
    label: "Martha's Skills",
    icon: <Terminal size={15} />,
  },
];

const OPERATIONS_CATEGORIES: CategoryDef[] = [
  {
    id: 'deployments',
    label: 'Deployments',
    icon: <Rocket size={15} />,
  },
  {
    id: 'fleet',
    label: 'Agent Fleet',
    icon: <Shield size={15} />,
  },
];

const PROJECT_CATEGORIES: CategoryDef[] = [
  {
    id: 'project-general',
    label: 'General',
    icon: <Settings2 size={15} />,
  },
  {
    id: 'project-repo',
    label: 'Repository',
    icon: <GitBranch size={15} />,
  },
  {
    id: 'project-agents',
    label: 'Agent Overrides',
    icon: <Layers size={15} />,
  },
  {
    id: 'project-tools',
    label: 'Data & Tools',
    icon: <Trash2 size={15} />,
  },
];

interface SettingsSideNavProps {
  activeCategory: SettingsCategory;
  onCategoryChange: (category: SettingsCategory) => void;
  activeSection?: string;
  onSectionClick?: (section: string) => void;
  projectName?: string;
}

export const SettingsSideNav: React.FC<SettingsSideNavProps> = ({
  activeCategory,
  onCategoryChange,
  activeSection,
  onSectionClick,
  projectName,
}) => {
  const allCategories = [...WORKSPACE_CATEGORIES, ...OPERATIONS_CATEGORIES, ...PROJECT_CATEGORIES];
  const activeDef = allCategories.find(c => c.id === activeCategory);

  const renderCategoryButton = (cat: CategoryDef, disabled = false) => (
    <button
      key={cat.id}
      onClick={() => !disabled && onCategoryChange(cat.id)}
      disabled={disabled}
      className={`
        flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors
        ${disabled
          ? 'text-subtle opacity-60 cursor-not-allowed'
          : activeCategory === cat.id
            ? 'bg-surface-subtle text-text'
            : 'text-muted hover:text-text hover:bg-surface-muted'
        }
      `}
    >
      <span className={`flex-shrink-0 ${disabled ? 'opacity-50' : 'opacity-90'}`}>{cat.icon}</span>
      {cat.label}
    </button>
  );

  return (
    <div className="flex flex-col gap-1 py-2">
      {/* Workspace group */}
      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-subtle">
        Workspace
      </div>
      {WORKSPACE_CATEGORIES.map(cat => renderCategoryButton(cat))}

      {/* Operations group */}
      <div className="mt-4 mb-1 border-t border-line" />
      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-subtle">
        Operations
      </div>
      {OPERATIONS_CATEGORIES.map(cat => renderCategoryButton(cat))}

      {/* Project group */}
      <div className="mt-4 mb-1 border-t border-line" />
      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-subtle">
        {projectName ? `Project (${projectName})` : 'Project'}
      </div>
      {!projectName ? (
        <div className="px-3 py-2 text-[12px] text-subtle italic">
          Select a project above
        </div>
      ) : (
        PROJECT_CATEGORIES.map(cat => renderCategoryButton(cat))
      )}

      {/* Section links for active category */}
      {activeDef?.sections && activeDef.sections.length > 0 && (
        <>
          <div className="mt-4 mb-1 border-t border-line" />
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-subtle">
            Sections
          </div>
          {activeDef.sections.map(section => (
            <button
              key={section}
              onClick={() => onSectionClick?.(section)}
              className={`
                flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] transition-colors
                ${activeSection === section
                  ? 'text-text font-medium'
                  : 'text-muted hover:text-text'
                }
              `}
            >
              <span className="w-1 h-1 rounded-full bg-current opacity-50" />
              {section}
            </button>
          ))}
        </>
      )}
    </div>
  );
};
